/*
Copyright (c) 2012-2024 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { Component, Fragment } from "react"
import { range, isEqual, compact } from "lodash"
import { Select, Button, Flex } from "@duxton-web/ui"
import { CloseCircleFilled } from "@duxton-web/icons"
import { TimeRangePicker } from "components/common"
import { openPeriodTypes } from "utils/section_helper"
import { ServiceDay, Period } from "types/api"

export const MAX_TIME_SLOTS = 3

interface TimingSelectorProps {
  serviceHour?: ServiceDay
  prefix: string
  form: WrappedFormUtils
  day: string
  disabled?: boolean
  isFirstSection?: boolean
  propagateValue?: ServiceDay | null
  onChange?(): void
  openPeriodText: { [key: string]: string }
  textMap: { [key: string]: string }
}

interface TimingSelectorState {
  openPeriodType?: string
  timeSlotCount: number
  periods: Period[]
}

const { OPEN_ALL_DAY, CLOSED_ALL_DAY, PERIOD } = openPeriodTypes

export class TimingSelector extends Component<
  TimingSelectorProps,
  TimingSelectorState
> {
  constructor(props: TimingSelectorProps) {
    super(props)
    this.state = {
      openPeriodType: CLOSED_ALL_DAY,
      timeSlotCount: 0,
      periods: [],
    }
  }

  componentDidMount() {
    const { serviceHour } = this.props
    this.setState({
      openPeriodType: serviceHour?.openPeriodType,
      timeSlotCount: serviceHour?.periods?.length ?? 0,
      periods: serviceHour?.periods ?? [],
    })
  }

  componentDidUpdate(prevProps: TimingSelectorProps) {
    const { propagateValue } = this.props
    if (propagateValue && !isEqual(propagateValue, prevProps.propagateValue)) {
      this.handleSetValues(propagateValue)
    }
  }

  handleAddTimeSlot = () => {
    const { timeSlotCount } = this.state
    this.setState({
      timeSlotCount: timeSlotCount + 1,
    })
    this.props?.onChange?.()
  }

  handleRemoveTimeSlot = (index: number) => {
    const { form, prefix } = this.props
    const { timeSlotCount } = this.state

    const { getFieldValue, setFieldsValue } = form
    const periodPath = `${prefix}.periods`

    const periods = getFieldValue(periodPath)

    periods.splice(index, 1)
    setFieldsValue({
      [periodPath]: periods,
    })
    this.setState({
      timeSlotCount: timeSlotCount - 1,
    })
    this.props?.onChange?.()
  }

  handleChangePeriodType = (openPeriodType: string) => {
    if (openPeriodType === PERIOD) {
      this.setState({
        openPeriodType,
        timeSlotCount: 1,
      })
    } else {
      this.setState({
        openPeriodType,
        timeSlotCount: 0,
      })
    }
    this.props?.onChange?.()
  }

  handleSetValues(value: ServiceDay) {
    const { form, prefix } = this.props
    const { setFieldsValue } = form
    this.setState(
      {
        openPeriodType: value.openPeriodType,
        timeSlotCount: value.periods?.length ?? 0,
        periods: value?.periods ?? [],
      },
      () => {
        setFieldsValue({
          [prefix]: value,
        })
        this.props?.onChange?.()
      },
    )
  }

  render() {
    const {
      serviceHour,
      day,
      form,
      disabled,
      prefix,
      onChange,
      openPeriodText,
      isFirstSection,
      textMap,
    } = this.props

    const { openPeriodType, timeSlotCount, periods } = this.state

    const { getFieldDecorator } = form

    const openPeriodTypePath = `${prefix}.openPeriodType`
    const periodPath = `${prefix}.periods`

    return (
      <Fragment>
        {getFieldDecorator(openPeriodTypePath, {
          initialValue: isFirstSection
            ? OPEN_ALL_DAY
            : serviceHour?.openPeriodType ?? CLOSED_ALL_DAY,
        })(
          <Select
            data-testid="period-select"
            disabled={disabled}
            css={css`
              width: 140px;
            `}
            size="small"
            onChange={this.handleChangePeriodType}
          >
            {compact([OPEN_ALL_DAY, CLOSED_ALL_DAY, PERIOD]).map(opt => (
              <Select.Option key={opt}>{openPeriodText[opt]}</Select.Option>
            ))}
          </Select>,
        )}
        {openPeriodType === PERIOD && (
          <Fragment>
            {range(0, timeSlotCount).map(index => (
              <Flex
                key={`${day}-${index}`}
                align="center"
                css={css`
                  margin-top: 8px;
                  margin-bottom: 16px;
                `}
              >
                <TimeRangePicker
                  data-testid="time-range-picker"
                  format="HH:mm"
                  width={58}
                  size="small"
                  start={periods?.[index]?.startTime ?? "00:00"}
                  end={periods?.[index]?.endTime ?? "24:00"}
                  formOptions={{
                    form,
                    startKey: `${periodPath}[${index}].startTime`,
                    endKey: `${periodPath}[${index}].endTime`,
                  }}
                  onChangeStartTime={onChange}
                  onChangeEndTime={onChange}
                />
                {timeSlotCount > 1 && (
                  <CloseCircleFilled
                    data-testid="remove-time-slot-icon"
                    css={css`
                      margin-left: 4px;
                    `}
                    onClick={() => this.handleRemoveTimeSlot(index)}
                  />
                )}
              </Flex>
            ))}
            {timeSlotCount < MAX_TIME_SLOTS && (
              <Button
                data-testid="add-time-slot-button"
                type="link"
                onClick={this.handleAddTimeSlot}
              >
                {textMap.timingAdd}
              </Button>
            )}
          </Fragment>
        )}
      </Fragment>
    )
  }
}
